import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import {
  DeleteOutlined,
  StopOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  PlayCircleOutlined,
  MoreOutlined,
  EyeOutlined,
} from "@ant-design/icons";
import {
  Button,
  Progress,
  Tag,
  Space,
  Modal,
  message,
  Typography,
  Tooltip,
  Dropdown,
  Menu,
  Spin,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import moment from "moment";

import {
  uploadTicketFile,
  getTicketImports,
  getTicketImportProgress,
  cancelTicketImport,
  deleteTicketImport,
  startTicketImport,
  downloadTicketImportErrors,
  downloadOriginalTicketFile,
  previewTicketImportJsonl,
} from "../../api/Services";
import { BusinessUniteContext } from "../../components/layout/Layout";
import JsonlViewerModal from "../../components/jsonlviewer/JsonlViewerModal";
import { JsonlFetchPage } from "../../components/jsonlviewer/jsonlUtils";
import ImportExperience from "../../components/importexperience/ImportExperience";

const { Text } = Typography;

interface ImportRecord {
  id: string;
  originalFilename: string;
  status: string;
  startedAt: string;
  finishedAt?: string;
  totalLines: number;
  processedLines: number;
  correct: number;
  incorrect: number;
  percentCompleted: number;
  resultSummary: string;
  userEmail: string;
  organizationName: string;
  fileKey: string;
}

const POLLING_INTERVAL = 5000;

const TicketImport: React.FC = () => {
  const { t } = useTranslation();
  const { contextValue: businessUnitSelected } =
    useContext(BusinessUniteContext);

  const [imports, setImports] = useState<ImportRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [autoStart, setAutoStart] = useState(false);
  const [progressPolling, setProgressPolling] = useState<{
    [key: string]: NodeJS.Timeout;
  }>({});
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalElements, setTotalElements] = useState(0);
  const [downloadingFiles, setDownloadingFiles] = useState<{
    [key: string]: boolean;
  }>({});
  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerProcessId, setViewerProcessId] = useState<string | null>(null);
  const [viewerTitle, setViewerTitle] = useState("");

  const loadImports = useCallback(
    async (page = 1, size = 10) => {
      setLoading(true);
      try {
        const response = await getTicketImports(page - 1, size);
        setImports(response.data.data.content || []);
        setTotalElements(response.data.data.totalElements || 0);
      } catch (error) {
        message.error(t("Error-LoadingImports"));
      } finally {
        setLoading(false);
      }
    },
    [t]
  );

  const handleOpenViewer = (processId: string, filename: string) => {
    setViewerProcessId(processId);
    setViewerTitle(`${t("Common-Visualize")}: ${filename}`);
    setViewerOpen(true);
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setViewerProcessId(null);
    setViewerTitle("");
  };

  const viewerFetchPage = useMemo<JsonlFetchPage | null>(() => {
    if (!viewerProcessId) {
      return null;
    }
    return async ({ page, size, search }) =>
      previewTicketImportJsonl(viewerProcessId, page, size, search);
  }, [viewerProcessId]);

  useEffect(() => {
    if (businessUnitSelected) {
      loadImports(currentPage, pageSize);
    }
  }, [businessUnitSelected, currentPage, pageSize, loadImports]);

  useEffect(() => {
    if (imports.length === 0) return;

    const processingImports = imports.filter(
      (imp) => imp.status === "PROCESSING"
    );

    processingImports.forEach((imp) => {
      if (!progressPolling[imp.id]) {
        const interval = setInterval(async () => {
          try {
            const response = await getTicketImportProgress(imp.id);
            const updatedImport = response.data;

            setImports((prev) =>
              prev.map((item) => (item.id === imp.id ? updatedImport : item))
            );

            if (
              ["COMPLETED", "CANCELLED", "ERROR"].includes(updatedImport.status)
            ) {
              clearInterval(interval);
              setProgressPolling((prev) => {
                const newPolling = { ...prev };
                delete newPolling[imp.id];
                return newPolling;
              });
            }
          } catch (error) {
            console.error("Error polling progress for import:", imp.id, error);
          }
        }, POLLING_INTERVAL);

        setProgressPolling((prev) => ({ ...prev, [imp.id]: interval }));
      }
    });

    Object.keys(progressPolling).forEach((id) => {
      const stillProcessing = processingImports.find((imp) => imp.id === id);
      if (!stillProcessing) {
        clearInterval(progressPolling[id]);
        setProgressPolling((prev) => {
          const newPolling = { ...prev };
          delete newPolling[id];
          return newPolling;
        });
      }
    });
  }, [imports, progressPolling]);
  useEffect(() => {
    return () => {
      Object.values(progressPolling).forEach(clearInterval);
    };
  }, [progressPolling]);

  const handleUpload: UploadProps["onChange"] = async (info) => {
    setFileList(info.fileList);

    if (info.file.status === "done") {
      message.success(`${info.file.name} ${t("Common-UploadSuccess")}`);
      setFileList([]);
      loadImports();

      if (autoStart && info.file.response?.data?.processId) {
        startProgressPolling(info.file.response.data.processId);
      }
    } else if (info.file.status === "error") {
      message.error(`${info.file.name} ${t("Common-UploadFailed")}`);
    }
  };

  const handleUploadRequest = async (options: any) => {
    const { file, onSuccess, onError } = options;

    setUploading(true);
    try {
      console.log("Uploading file:", file.name);
      const response = await uploadTicketFile(file, autoStart);
      console.log("Upload response:", response);
      onSuccess(response.data, file);

      setFileList([]);

      loadImports();

      if (autoStart && response.data.processId) {
        startProgressPolling(response.data.processId);
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      onError(error);
      message.error(error.response?.data?.message || t("Error-UploadFailed"));
    } finally {
      setUploading(false);
    }
  };

  const startProgressPolling = (processId: string) => {
    const interval = setInterval(async () => {
      try {
        const response = await getTicketImportProgress(processId);
        const updatedImport = response.data;

        setImports((prev) =>
          prev.map((imp) => (imp.id === processId ? updatedImport : imp))
        );

        if (
          ["COMPLETED", "CANCELLED", "ERROR"].includes(updatedImport.status)
        ) {
          clearInterval(interval);
          setProgressPolling((prev) => {
            const newPolling = { ...prev };
            delete newPolling[processId];
            return newPolling;
          });
        }
      } catch (error) {
        console.error("Error polling progress:", error);
      }
    }, 2000);

    setProgressPolling((prev) => ({ ...prev, [processId]: interval }));
  };

  const handleCancel = async (processId: string) => {
    try {
      await cancelTicketImport(processId);
      message.success(t("Common-Cancelled"));
      loadImports();
    } catch (error) {
      message.error(t("Error-CancelFailed"));
    }
  };

  const handleDelete = async (processId: string) => {
    Modal.confirm({
      title: t("Common-ConfirmDelete"),
      content: t("Common-DeleteWarning"),
      okText: t("Common-Delete"),
      okType: "danger",
      cancelText: t("Common-Cancel"),
      onOk: async () => {
        try {
          await deleteTicketImport(processId);
          message.success(t("Common-Deleted"));
          loadImports(currentPage, pageSize);
        } catch (error) {
          message.error(t("Error-DeleteFailed"));
        }
      },
    });
  };

  const handleDownloadErrors = async (processId: string) => {
    const downloadKey = `errors_${processId}`;
    if (downloadingFiles[downloadKey]) return;

    setDownloadingFiles((prev) => ({ ...prev, [downloadKey]: true }));

    try {
      // Get secure download URL with temporary token
      const response = await downloadTicketImportErrors(processId);
      const downloadUrl = response.downloadUrl;
      const filename = response.filename;

      message.success(t("Common-DownloadStarted"));

      fetch(downloadUrl, {
        method: "GET",
        // No need for Authorization header as the URL contains a temporary token
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.blob();
        })
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error("Download failed:", error);
          message.error(t("Error-DownloadFailed"));
        });
    } catch (error) {
      console.error("Download failed:", error);
      message.error(t("Error-DownloadFailed"));
    } finally {
      setDownloadingFiles((prev) => ({ ...prev, [downloadKey]: false }));
    }
  };

  const handleDownloadOriginal = async (
    processId: string,
    filename: string
  ) => {
    const downloadKey = `original_${processId}`;
    if (downloadingFiles[downloadKey]) return;

    setDownloadingFiles((prev) => ({ ...prev, [downloadKey]: true }));

    try {
      const response = await downloadOriginalTicketFile(processId);
      const downloadUrl = response.downloadUrl;
      const secureFilename = response.filename;

      message.success(t("Common-DownloadStarted"));

      fetch(downloadUrl, {
        method: "GET",
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.blob();
        })
        .then((blob) => {
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = secureFilename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error("Download failed:", error);
          message.error(t("Error-DownloadFailed"));
        });
    } catch (error) {
      console.error("Download failed:", error);
      message.error(t("Error-DownloadFailed"));
    } finally {
      setDownloadingFiles((prev) => ({ ...prev, [downloadKey]: false }));
    }
  };

  const handleStart = async (processId: string) => {
    try {
      await startTicketImport(processId);
      message.success(t("Common-Started"));
      loadImports();
    } catch (error) {
      message.error(t("Error-StartFailed"));
    }
  };

  const getStatusTag = (status: string) => {
    switch (status) {
      case "PENDING":
        return (
          <Tag color="default" icon={<ClockCircleOutlined />}>
            {t("Common-Pending")}
          </Tag>
        );
      case "PROCESSING":
        return (
          <Tag color="processing">
            <Spin size="small" style={{ marginRight: 6 }} />
            {t("Common-Processing")}
          </Tag>
        );
      case "COMPLETED":
        return (
          <Tag color="success" icon={<CheckCircleOutlined />}>
            {t("Common-Completed")}
          </Tag>
        );
      case "ERROR":
        return (
          <Tag color="error" icon={<ExclamationCircleOutlined />}>
            {t("Common-Error")}
          </Tag>
        );
      case "CANCELLED":
        return (
          <Tag color="orange" icon={<StopOutlined />}>
            {t("Common-Cancelled")}
          </Tag>
        );
      default:
        return <Tag color="default">{status}</Tag>;
    }
  };

  const renderFeaturedActions = (record: ImportRecord) => (
    <Space wrap>
      {record.status === "PENDING" && (
        <Button
          type="primary"
          size="small"
          icon={<PlayCircleOutlined />}
          onClick={() => handleStart(record.id)}
        >
          {t("Common-Start")}
        </Button>
      )}
      {record.status === "PROCESSING" && (
        <Button
          danger
          size="small"
          icon={<StopOutlined />}
          onClick={() => handleCancel(record.id)}
        >
          {t("Common-Cancel")}
        </Button>
      )}
      {record.incorrect > 0 && ["COMPLETED", "ERROR"].includes(record.status) && (
        <Button
          size="small"
          icon={<DownloadOutlined />}
          loading={downloadingFiles[`errors_${record.id}`]}
          onClick={() => handleDownloadErrors(record.id)}
        >
          {t("Common-DownloadErrors")}
        </Button>
      )}
      <Button
        size="small"
        icon={<EyeOutlined />}
        onClick={() => handleOpenViewer(record.id, record.originalFilename)}
      >
        {t("Common-Visualize")}
      </Button>
      <Button
        size="small"
        icon={<DownloadOutlined />}
        loading={downloadingFiles[`original_${record.id}`]}
        onClick={() =>
          handleDownloadOriginal(record.id, record.originalFilename)
        }
      >
        {t("Import-DownloadOriginal")}
      </Button>
    </Space>
  );

  const instructionDetails = (
    <div style={{ fontSize: 13 }}>
      <div>{t("Common-TicketImportDescription")}</div>
      <ul style={{ margin: "8px 0", paddingLeft: 18 }}>
        <li>{t("Common-SupportedFormats")}: JSONL, ZIP</li>
        <li>{t("Common-RequiredFields")}: Ticket data in JSONL format</li>
        <li>{t("Common-MaxFileSize")}: 100MB</li>
      </ul>

      <div style={{ marginTop: 16 }}>
        <strong>{t("Common-JsonlFormatExample")}</strong>
        <div
          style={{
            backgroundColor: "#f5f5f5",
            border: "1px solid #d9d9d9",
            borderRadius: "6px",
            padding: "12px",
            fontFamily: "monospace",
            fontSize: "11px",
            marginTop: "8px",
            overflowX: "auto",
            whiteSpace: "pre-wrap",
            wordBreak: "break-all",
          }}
        >
          {t("Common-JsonlFormatSample")}
        </div>
      </div>

      <div style={{ marginTop: 12, fontSize: 12, color: "#666" }}>
        <strong>{t("Common-JsonlKeyFields")}</strong>
        <ul style={{ margin: "4px 0", paddingLeft: 18 }}>
          <li
            dangerouslySetInnerHTML={{
              __html: t("Common-JsonlKeyFieldSalePoint"),
            }}
          />
          <li
            dangerouslySetInnerHTML={{ __html: t("Common-JsonlKeyFieldSkus") }}
          />
          <li
            dangerouslySetInnerHTML={{
              __html: t("Common-JsonlKeyFieldPayments"),
            }}
          />
          <li
            dangerouslySetInnerHTML={{
              __html: t("Common-JsonlKeyFieldPromotions"),
            }}
          />
        </ul>
      </div>
    </div>
  );

  const columns: ColumnsType<ImportRecord> = [
    {
      title: t("Common-Filename"),
      dataIndex: "originalFilename",
      key: "originalFilename",
      render: (text: string) => (
        <Tooltip title={text} placement="topLeft">
          <Text
            strong
            ellipsis
            style={{ maxWidth: 165, display: "inline-block" }}
          >
            {text}
          </Text>
        </Tooltip>
      ),
      width: 190,
    },
    {
      title: t("Common-Status"),
      dataIndex: "status",
      key: "status",
      render: (status: string) => getStatusTag(status),
      width: 130,
    },
    {
      title: t("Common-Progress"),
      dataIndex: "percentCompleted",
      key: "percentCompleted",
      render: (percent: number, record: ImportRecord) => (
        <div style={{ minWidth: 70, textAlign: "center" }}>
          <Progress
            percent={percent}
            size="small"
            status={record.status === "ERROR" ? "exception" : undefined}
            showInfo={false}
          />
          <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>
            {typeof percent === "number" ? percent.toFixed(2) + "%" : ""}
          </div>
        </div>
      ),
      width: 150,
    },
    {
      title: t("Common-Results"),
      key: "results",
      render: (_, record) => (
        <Space direction="vertical" size={0} style={{ fontSize: 12 }}>
          <Text type="secondary">
            {t("Common-Total")}: {record.totalLines}
          </Text>
          <Text type="success">
            {t("Common-Correct")}: {record.correct}
          </Text>
          <Text type="danger">
            {t("Common-Errors")}: {record.incorrect}
          </Text>
        </Space>
      ),
      width: 130,
      responsive: ["lg"],
    },
    {
      title: t("Common-Started"),
      dataIndex: "startedAt",
      key: "startedAt",
      render: (date: string) => moment(date).format("DD/MM/YYYY HH:mm:ss"),
      width: 165,
      responsive: ["xl"],
    },
    {
      title: t("Common-Finished"),
      dataIndex: "finishedAt",
      key: "finishedAt",
      render: (date: string) =>
        date ? moment(date).format("DD/MM/YYYY HH:mm:ss") : "-",
      width: 165,
      responsive: ["xxl"],
    },
    {
      title: t("Common-Actions"),
      key: "actions",
      align: "center",
      render: (_, record) => {
        const menu = (
          <Menu>
            {record.status === "PENDING" && (
              <Menu.Item key="start" onClick={() => handleStart(record.id)}>
                <PlayCircleOutlined style={{ marginRight: 8 }} />
                {t("Common-Start")}
              </Menu.Item>
            )}
            {record.status === "PROCESSING" && (
              <Menu.Item key="cancel" onClick={() => handleCancel(record.id)}>
                <StopOutlined style={{ marginRight: 8 }} />
                {t("Common-Cancel")}
              </Menu.Item>
            )}
            {record.incorrect > 0 &&
              (record.status === "COMPLETED" || record.status === "ERROR") && (
                <Menu.Item
                  key="download"
                  onClick={() => handleDownloadErrors(record.id)}
                  disabled={downloadingFiles[`errors_${record.id}`]}
                >
                  {downloadingFiles[`errors_${record.id}`] ? (
                    <Spin size="small" style={{ marginRight: 8 }} />
                  ) : (
                    <DownloadOutlined style={{ marginRight: 8 }} />
                  )}
                  {t("Common-DownloadErrors")}
                </Menu.Item>
              )}
            <Menu.Item
              key="visualize"
              onClick={() =>
                handleOpenViewer(record.id, record.originalFilename)
              }
            >
              <EyeOutlined style={{ marginRight: 8 }} />
              {t("Common-Visualize")}
            </Menu.Item>
            <Menu.Item
              key="downloadOriginal"
              onClick={() =>
                handleDownloadOriginal(record.id, record.originalFilename)
              }
              disabled={downloadingFiles[`original_${record.id}`]}
            >
              {downloadingFiles[`original_${record.id}`] ? (
                <Spin size="small" style={{ marginRight: 8 }} />
              ) : (
                <DownloadOutlined style={{ marginRight: 8 }} />
              )}
              {t("Common-Download")}
            </Menu.Item>
            <Menu.Item
              key="delete"
              onClick={() => handleDelete(record.id)}
              danger
            >
              <DeleteOutlined style={{ marginRight: 8 }} />
              {t("Common-Delete")}
            </Menu.Item>
          </Menu>
        );
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <Dropdown
              overlay={menu}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button icon={<MoreOutlined />} size="small" />
            </Dropdown>
          </div>
        );
      },
      width: 72,
    },
  ];

  return (
    <>
      <ImportExperience
        title={t("Common-TicketImport")}
        instructions={instructionDetails}
        accept=".jsonl,.zip"
        uploading={uploading}
        fileList={fileList}
        onUploadChange={handleUpload}
        onUploadRequest={handleUploadRequest}
        autoStart={autoStart}
        onAutoStartChange={setAutoStart}
        records={imports}
        loading={loading}
        columns={columns}
        onRefresh={() => void loadImports(currentPage, pageSize)}
        renderFeaturedActions={renderFeaturedActions}
        onDelete={(record) => handleDelete(record.id)}
        pagination={{
          current: currentPage,
          pageSize: pageSize,
          total: totalElements,
          showSizeChanger: true,
          showQuickJumper: true,
          onChange: (page, size) => {
            setCurrentPage(page);
            setPageSize(size);
          },
          onShowSizeChange: (current, size) => {
            setPageSize(size);
            setCurrentPage(1);
          },
          showTotal: (total, range) =>
            t("Common-PaginationInfo", {
              from: range[0],
              to: range[1],
              total,
            }),
          locale: {
            items_per_page: t("Common-ItemsPerPage"),
            jump_to: t("Common-GoTo"),
            page: t("Common-Page"),
          },
          responsive: true,
        }}
      />

      {viewerFetchPage ? (
        <JsonlViewerModal
          visible={viewerOpen}
          onClose={handleCloseViewer}
          title={viewerTitle}
          fetchPage={viewerFetchPage}
        />
      ) : null}
    </>
  );
};

export default TicketImport;
